import {
  PanelLeft,
  FileText,
  Building2,
  BookOpen,
  FolderOpen,
  MessageSquare,
  FileSearch,
  ArrowRight,
  FileEdit
} from 'lucide-react';

interface Feature {
  id: string;
  icon: typeof PanelLeft;
  title: string;
  description: string;
  location: string;
  color: string;
}

const FEATURES: Feature[] = [
  {
    id: 'sidebar',
    icon: PanelLeft,
    title: 'Sidebar Navigation',
    description: 'Switch between different views: Documents, USPTO Data, Resources, and Sessions',
    location: 'Left side of your screen',
    color: 'blue'
  },
  {
    id: 'active-docs',
    icon: FileText,
    title: 'Active Documents',
    description: 'View and manage all documents currently loaded in your session',
    location: 'Sidebar > Documents tab',
    color: 'green'
  },
  {
    id: 'chat',
    icon: MessageSquare,
    title: 'AI Chat Area',
    description: 'Ask questions and get AI-powered insights about your documents and patent law',
    location: 'Center of your screen',
    color: 'purple'
  },
  {
    id: 'drafts',
    icon: FileEdit,
    title: 'Drafts',
    description: 'Create, edit, and manage draft documents with AI assistance and professional formatting',
    location: 'Top right icon or Sidebar > Drafts',
    color: 'pink'
  },
  {
    id: 'viewers',
    icon: FileSearch,
    title: 'Document Viewers',
    description: 'Click on MPEP, CFR, or USC sections to open them in a side-by-side viewer',
    location: 'Opens on the right side',
    color: 'amber'
  },
  {
    id: 'uspto',
    icon: Building2,
    title: 'USPTO Data',
    description: 'View detailed information about loaded patent applications and their status',
    location: 'Sidebar > USPTO Data tab',
    color: 'indigo'
  },
  {
    id: 'resources',
    icon: BookOpen,
    title: 'Resources',
    description: 'Browse and search through MPEP, 37 CFR, 35 USC, and other patent resources',
    location: 'Sidebar > Resources tab',
    color: 'teal'
  },
  {
    id: 'sessions',
    icon: FolderOpen,
    title: 'Sessions',
    description: 'Save your work and return to it later by organizing into named sessions',
    location: 'Sidebar > Sessions tab',
    color: 'orange'
  }
];

export function FeaturesStep() {
  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; icon: string }> = {
      blue: { bg: 'bg-blue-100', icon: 'text-blue-600' },
      green: { bg: 'bg-green-100', icon: 'text-green-600' },
      purple: { bg: 'bg-purple-100', icon: 'text-purple-600' },
      pink: { bg: 'bg-pink-100', icon: 'text-pink-600' },
      amber: { bg: 'bg-amber-100', icon: 'text-amber-600' },
      indigo: { bg: 'bg-indigo-100', icon: 'text-indigo-600' },
      teal: { bg: 'bg-teal-100', icon: 'text-teal-600' },
      orange: { bg: 'bg-orange-100', icon: 'text-orange-600' }
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Explore the Interface</h2>
        <p className="text-gray-600">
          Here's a quick tour of the key features you'll use in Patent Loop
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {FEATURES.map((feature) => {
          const Icon = feature.icon;
          const colors = getColorClasses(feature.color);

          return (
            <div
              key={feature.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex gap-3">
                <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{feature.description}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <ArrowRight className="w-3 h-3" />
                    <span>{feature.location}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
        <p className="text-sm text-gray-700">
          <strong>Tip:</strong> You can explore these features at your own pace after completing this tour
        </p>
      </div>
    </div>
  );
}
